import React, { useState } from "react";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Box, Button, Checkbox, FormControlLabel, Popover } from "@mui/material";

interface MultiSelectDropdownProps {
  label: string;
  options: readonly string[];
  selected: string[];
  onChange: (value: string[]) => void;
  tone: "blue" | "green";
}

export const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  label,
  options,
  selected,
  onChange,
  tone
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const hasSelection = selected.length > 0;

  const toneStyles =
    tone === "blue"
      ? { activeBg: "#E3F0FF", activeBorder: "#3B82F6", activeColor: "#3B82F6" }
      : { activeBg: "#E8F5F0", activeBorder: "#496682", activeColor: "#496682" };

  return (
    <Box sx={{ position: "relative" }}>
      <Button
        onClick={(e) => setAnchorEl(anchorEl ? null : e.currentTarget)}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          p: "7px 12px",
          borderRadius: "999px",
          minWidth: 0,
          border: `1px solid ${hasSelection ? toneStyles.activeBorder : "#dbe8f5"}`,
          bgcolor: hasSelection ? toneStyles.activeBg : "#FFFFFF",
          color: hasSelection ? toneStyles.activeColor : "#496682",
          fontSize: 12,
          fontWeight: 700,
          textTransform: "none",
          "&:hover": { bgcolor: hasSelection ? toneStyles.activeBg : "#f6f8fc" }
        }}
      >
        {label}
        {hasSelection && (
          <Box
            component="span"
            sx={{
              bgcolor: toneStyles.activeColor,
              color: "#FFFFFF",
              borderRadius: "999px",
              px: "5px",
              fontSize: 10,
              fontWeight: 800,
              lineHeight: "16px"
            }}
          >
            {selected.length}
          </Box>
        )}
        <Box component="span" sx={{ fontSize: 18, color: "#6B7280", display: "flex" }}>
          <KeyboardArrowDownIcon
            sx={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}
          />
        </Box>
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: "6px",
              borderRadius: "10px",
              border: "1px solid #dbe8f5",
              boxShadow: "0 12px 32px rgba(7,31,53,0.12)",
              minWidth: 220,
              maxHeight: 280,
              overflowY: "auto"
            }
          }
        }}
      >
        {options.map((option) => {
          const checked = selected.includes(option);
          return (
            <FormControlLabel
              key={option}
              control={
                <Checkbox
                  checked={checked}
                  onChange={(e) => {
                    onChange(e.target.checked ? [...selected, option] : selected.filter((v) => v !== option));
                  }}
                  sx={{ p: "4px", "& .MuiSvgIcon-root": { fontSize: 18 } }}
                />
              }
              label={option}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                m: 0,
                p: "5px 14px",
                bgcolor: checked ? "#E8F5F0" : "transparent",
                "& .MuiFormControlLabel-label": {
                  fontSize: 13,
                  color: "#071f35",
                  fontWeight: checked ? 600 : 400,
                  maxWidth: 180,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap"
                }
              }}
            />
          );
        })}
        {hasSelection && (
          <Box sx={{ borderTop: "1px solid #dbe8f5", p: "6px 14px" }}>
            <Button
              onClick={() => {
                onChange([]);
                setAnchorEl(null);
              }}
              sx={{ color: "#d93e3e", fontSize: 12, fontWeight: 600, textTransform: "none", p: 0, minWidth: 0 }}
            >
              Clear {label.toLowerCase()}
            </Button>
          </Box>
        )}
      </Popover>
    </Box>
  );
};
